import React, { useMemo, useState } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { html as htmlLang } from '@codemirror/lang-html';
import { FileUp, AlertTriangle, CheckCircle2, Copy, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { validateSimHtml } from '../../lib/simFrame';
import { CHATGPT_GUIDE } from './chatgptGuide';

export const SimCodeEditor = ({ value, onChange }) => {
  const [guide, setGuide] = useState(false);
  const issues = useMemo(() => validateSimHtml(value), [value]);
  const ext = useMemo(() => [htmlLang()], []);
  const loadFile = (file) => {
    if (!file) return;
    if (file.size > 3_000_000) { toast.error('حجم الملف أكبر من 3MB'); return; }
    file.text().then(onChange);
  };
  const copy = () => navigator.clipboard?.writeText(CHATGPT_GUIDE).then(() => toast.success('تم نسخ التعليمات'));
  return (
    <div className="space-y-3" data-testid="sim-code-editor">
      <div className="flex flex-wrap items-center gap-2">
        <label className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-blue-300 text-blue-600 text-sm font-semibold cursor-pointer hover:bg-blue-50">
          <FileUp className="w-4 h-4" />رفع ملف HTML
          <input data-testid="sim-code-file" type="file" accept=".html,.htm,text/html" hidden onChange={(e) => loadFile(e.target.files?.[0])} />
        </label>
        <button type="button" data-testid="sim-guide-toggle" onClick={() => setGuide(!guide)} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg border border-slate-200 text-slate-700 text-sm font-semibold">
          تعليمات لـ ChatGPT<ChevronDown className={`w-4 h-4 transition-transform ${guide ? 'rotate-180' : ''}`} />
        </button>
        <span className="ms-auto text-xs text-slate-400" dir="ltr">{(value || '').length.toLocaleString('en-US')} chars</span>
      </div>
      {guide && (
        <div data-testid="sim-guide" className="rounded-xl border border-slate-200 bg-slate-50 p-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-bold text-slate-800">انسخ هذا النص وأرسله لـ ChatGPT مع وصف المحاكاة التي تريدها</p>
            <button type="button" data-testid="sim-guide-copy" onClick={copy} className="inline-flex items-center gap-1 text-blue-700 text-sm font-bold"><Copy className="w-4 h-4" />نسخ</button>
          </div>
          <pre dir="ltr" className="text-[11px] leading-5 whitespace-pre-wrap max-h-60 overflow-auto text-left text-slate-700">{CHATGPT_GUIDE}</pre>
        </div>
      )}
      <div dir="ltr" className="rounded-xl overflow-hidden border border-slate-200 text-left" data-testid="sim-code-input">
        <CodeMirror value={value} height="420px" extensions={ext} onChange={onChange} basicSetup={{ lineNumbers: true, foldGutter: true }}
          placeholder="<!doctype html> ... </html>" />
      </div>
      <div data-testid="sim-code-validation" className={`rounded-xl px-3 py-2 text-sm ${issues.length ? 'bg-amber-50 text-amber-800' : 'bg-emerald-50 text-emerald-800'}`}>
        {issues.length ? issues.map(i => <p key={i} className="flex items-center gap-1.5"><AlertTriangle className="w-4 h-4 shrink-0" />{i}</p>)
          : <p className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" />الكود جاهز. افتح تبويب «معاينة» للتجربة.</p>}
      </div>
    </div>
  );
};
